import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Input, 
  Space, 
  Tag, 
  Typography, 
  Row, 
  Col, 
  Select, 
  Modal, 
  Form, 
  message,
  Switch,
  InputNumber,
} from 'antd';
import { 
  SearchOutlined, 
  PlusOutlined, 
  ReloadOutlined, 
  EditOutlined, 
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { dealerService } from '../../services/dealerService';
import { Dealer, DealerVO, DealerRequest } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import type { ColumnsType } from 'antd/es/table';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;
const { TextArea } = Input;

const DealerList: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<DealerVO | null>(null);
  const [dataSource, setDataSource] = useState<DealerVO[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchParams, setSearchParams] = useState<any>({});
  const [form] = Form.useForm();

  // 加载数据
  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [pagination.current, pagination.pageSize, searchParams, user]);

  const loadData = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const response = await dealerService.queryDealers({
        pageNo: pagination.current - 1, // Spring Data JPA 从0开始
        pageSize: pagination.pageSize,
        sortField: 'createTime',
        sortDirection: 'desc',
        ...searchParams,
      });

      if (response.data) {
        setDataSource(response.data.content || []);
        setPagination(prev => ({
          ...prev,
          total: response.data.totalElements || 0,
        }));
      }
    } catch (error) {
      console.error('加载经销商列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<DealerVO> = [
    {
      title: '经销商ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
    },
    {
      title: '经销商编码',
      dataIndex: 'dealerCode',
      key: 'dealerCode',
      width: 150,
    },
    {
      title: '经销商名称',
      dataIndex: 'dealerName',
      key: 'dealerName',
      width: 200,
    },
    {
      title: '经销商类型',
      dataIndex: 'dealerTypeText',
      key: 'dealerType',
      width: 120,
      render: (text: string, record: DealerVO) => (
        <Tag color={record.dealerType === 1 ? 'gold' : 'blue'}>
          {text || (record.dealerType === 1 ? '超级经销商' : '普通经销商')}
        </Tag>
      ),
    },
    {
      title: '经销商等级',
      dataIndex: 'dealerLevel',
      key: 'dealerLevel',
      width: 100,
    },
    {
      title: '联系人',
      dataIndex: 'contactPerson',
      key: 'contactPerson',
      width: 120,
    },
    {
      title: '联系电话',
      dataIndex: 'contactPhone',
      key: 'contactPhone',
      width: 140,
    },
    {
      title: '状态',
      dataIndex: 'statusText',
      key: 'status',
      width: 100,
      render: (text: string, record: DealerVO) => (
        <Tag color={record.status === 1 ? 'success' : 'error'}>
          {text || (record.status === 1 ? '启用' : '禁用')}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 180,
      render: (text: string) => text ? new Date(text).toLocaleString('zh-CN') : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 280,
      fixed: 'right',
      render: (_, record: DealerVO) => (
        <Space size="small">
          <Button 
            type="link" 
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          {record.status === 1 ? (
            <Button 
              type="link" 
              size="small"
              danger
              icon={<CloseCircleOutlined />}
              onClick={() => handleToggleStatus(record, 0)}
            >
              禁用
            </Button>
          ) : (
            <Button 
              type="link" 
              size="small"
              icon={<CheckCircleOutlined />}
              onClick={() => handleToggleStatus(record, 1)}
            >
              启用
            </Button>
          )}
          <Button 
            type="link" 
            size="small" 
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  const handleSearch = (value: string) => {
    setSearchParams((prev: any) => ({ ...prev, dealerName: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleFilterChange = (key: string, value: any) => {
    setSearchParams((prev: any) => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleRefresh = () => {
    loadData();
  };

  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({ status: 1, dealerLevel: 1 }); // 默认值
    setIsModalVisible(true);
  };

  const handleEdit = (record: DealerVO) => {
    setEditingRecord(record);
    form.setFieldsValue({
      dealerCode: record.dealerCode,
      dealerName: record.dealerName,
      dealerLevel: record.dealerLevel,
      adminId: record.adminId,
      parentDealerId: record.parentDealerId,
      contactPerson: record.contactPerson,
      contactPhone: record.contactPhone,
      contactEmail: record.contactEmail,
      address: record.address,
      status: record.status,
      remark: record.remark,
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (record: DealerVO) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除经销商"${record.dealerName}"吗？`,
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        try {
          await dealerService.deleteDealer(record.id, user?.username || 'admin');
          message.success('删除成功');
          loadData();
        } catch (error) {
          console.error('删除失败:', error);
        }
      },
    });
  };

  const handleToggleStatus = async (record: DealerVO, status: number) => {
    try {
      if (status === 1) {
        await dealerService.enableDealer(record.id, user?.username || 'admin');
        message.success('启用成功');
      } else {
        await dealerService.disableDealer(record.id, user?.username || 'admin');
        message.success('禁用成功');
      }
      loadData();
    } catch (error) {
      console.error('操作失败:', error);
    }
  };

  const handleBatchUpdateStatus = (status: number) => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要操作的经销商');
      return;
    }

    Modal.confirm({
      title: status === 1 ? '确认批量启用' : '确认批量禁用',
      content: `确定要${status === 1 ? '启用' : '禁用'}选中的 ${selectedRowKeys.length} 个经销商吗？`,
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        try {
          await dealerService.batchUpdateStatus(
            selectedRowKeys as number[], 
            status, 
            user?.username || 'admin'
          );
          message.success(`批量${status === 1 ? '启用' : '禁用'}成功`);
          setSelectedRowKeys([]);
          loadData();
        } catch (error) {
          console.error('批量操作失败:', error);
        }
      },
    });
  };

  const handleTableChange = (newPagination: any) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    }));
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const dealerData: DealerRequest = {
        dealerCode: values.dealerCode,
        dealerName: values.dealerName,
        dealerLevel: values.dealerLevel,
        adminId: values.adminId,
        parentDealerId: values.parentDealerId || null,
        contactPerson: values.contactPerson,
        contactPhone: values.contactPhone,
        contactEmail: values.contactEmail,
        address: values.address,
        status: values.status,
        remark: values.remark,
      };

      if (editingRecord) {
        // 更新
        await dealerService.updateDealerDto(
          editingRecord.id, 
          dealerData, 
          user?.username || 'admin'
        );
        message.success('更新成功');
      } else {
        // 创建
        await dealerService.createDealerDto(
          dealerData, 
          user?.username || 'admin'
        );
        message.success('创建成功');
      }

      setIsModalVisible(false);
      form.resetFields();
      loadData();
    } catch (error) {
      console.error('保存失败:', error);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys: React.Key[]) => {
      setSelectedRowKeys(selectedKeys);
    },
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={4} style={{ margin: 0 }}>经销商管理</Title>
          </Col>
        </Row>

        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col>
            <Search
              placeholder="搜索经销商名称"
              allowClear
              onSearch={handleSearch}
              style={{ width: 250 }}
              enterButton={<SearchOutlined />}
            />
          </Col>
          <Col>
            <Select
              placeholder="经销商类型"
              allowClear
              style={{ width: 150 }}
              onChange={(value) => handleFilterChange('dealerType', value)}
            >
              <Option value={1}>超级经销商</Option>
              <Option value={0}>普通经销商</Option>
            </Select>
          </Col>
          <Col>
            <Select
              placeholder="状态"
              allowClear
              style={{ width: 120 }}
              onChange={(value) => handleFilterChange('status', value)}
            >
              <Option value={1}>启用</Option>
              <Option value={0}>禁用</Option>
            </Select>
          </Col>
          <Col flex="auto" style={{ textAlign: 'right' }}>
            <Space>
              {selectedRowKeys.length > 0 && (
                <>
                  <Button 
                    icon={<CheckCircleOutlined />}
                    onClick={() => handleBatchUpdateStatus(1)}
                  >
                    批量启用
                  </Button>
                  <Button 
                    danger
                    icon={<CloseCircleOutlined />}
                    onClick={() => handleBatchUpdateStatus(0)}
                  >
                    批量禁用
                  </Button>
                </>
              )}
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                onClick={handleAdd}
              >
                新增经销商
              </Button>
              <Button 
                icon={<ReloadOutlined />} 
                onClick={handleRefresh}
              >
                刷新
              </Button>
            </Space>
          </Col>
        </Row>

        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={dataSource}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1500 }}
        />
      </Card>

      {/* 新增/编辑对话框 */}
      <Modal
        title={editingRecord ? '编辑经销商' : '新增经销商'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={700}
        okText="确定"
        cancelText="取消"
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ status: 1, dealerLevel: 1 }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="经销商编码"
                name="dealerCode"
                rules={[
                  { required: true, message: '请输入经销商编码' },
                  { max: 32, message: '经销商编码不能超过32个字符' },
                ]}
              >
                <Input 
                  placeholder="请输入经销商编码" 
                  disabled={!!editingRecord}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="经销商名称"
                name="dealerName"
                rules={[
                  { required: true, message: '请输入经销商名称' },
                  { max: 64, message: '经销商名称不能超过64个字符' },
                ]}
              >
                <Input placeholder="请输入经销商名称" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="经销商等级"
                name="dealerLevel"
                rules={[{ required: true, message: '请输入经销商等级' }]}
              >
                <InputNumber 
                  placeholder="请输入经销商等级" 
                  min={1}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="管理员ID"
                name="adminId"
                rules={[{ required: true, message: '请输入管理员ID' }]}
              >
                <InputNumber 
                  placeholder="请输入管理员ID" 
                  min={1}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="父经销商ID"
                name="parentDealerId"
              >
                <InputNumber 
                  placeholder="请输入父经销商ID（可选）" 
                  min={1}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="状态"
                name="status"
                rules={[{ required: true, message: '请选择状态' }]}
              >
                <Select placeholder="请选择状态">
                  <Option value={1}>启用</Option>
                  <Option value={0}>禁用</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="联系人"
                name="contactPerson"
                rules={[{ max: 32, message: '联系人不能超过32个字符' }]}
              >
                <Input placeholder="请输入联系人" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="联系电话"
                name="contactPhone"
                rules={[
                  { max: 20, message: '联系电话不能超过20个字符' },
                  { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码' },
                ]}
              >
                <Input placeholder="请输入联系电话" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="联系邮箱"
            name="contactEmail"
            rules={[
              { max: 64, message: '联系邮箱不能超过64个字符' },
              { type: 'email', message: '请输入正确的邮箱格式' },
            ]}
          >
            <Input placeholder="请输入联系邮箱" />
          </Form.Item>

          <Form.Item
            label="地址"
            name="address"
            rules={[{ max: 256, message: '地址不能超过256个字符' }]}
          >
            <Input placeholder="请输入地址" />
          </Form.Item>

          <Form.Item
            label="备注"
            name="remark"
            rules={[{ max: 255, message: '备注不能超过255个字符' }]}
          >
            <TextArea 
              rows={3} 
              placeholder="请输入备注信息" 
              maxLength={255}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DealerList;
